import type { ErrorSubscriptionResponse, IWebSocketClient, RequestStream, SubscriptionCallback } from '@talos/kyoko';
import { logger } from '@talos/kyoko';
import { ReplaySubject, Subject, timer, type Observable, type Subscription as RxJsSubscription } from 'rxjs';
import { repeat, retry } from 'rxjs/operators';
import { webSocket, type WebSocketSubject } from 'rxjs/webSocket';
import { COIN_METRICS_ASSET_METRIC_TICK } from './coinMetricsConstants';
import {
  buildConnectionUrl,
  buildReverseIndex,
  buildTickResponse,
  canonicalizeAssetsAndMetrics,
  chunkAssetsByUrlLength,
  computeBackoffDelayMs,
  filterStreamsByAddressToAssets,
  getMetricNamesForAddress,
  groupStreamsByFrequency,
  parseAssetMetricTick,
  parseCoinMetricsErrorEnvelope,
  partitionAddressesToAvoidCrossSubscriberEviction,
  validateCoinMetricsRequestStream,
  type CoinMetricsRequestStream,
} from './coinMetricsSubscriptionMerge';

const WS_INITIAL_RETRY_MS = 1000;
const WS_MAX_RETRY_MS = 15000;
const WEBSOCKET_ERROR_SUBSCRIPTION_BLOCKED = 100;
const WEBSOCKET_ERROR_CONNECTION_FAILING = 101;
const WEBSOCKET_ERROR_CONNECTION_LIMIT_EXCEEDED = 102;
const WEBSOCKET_ERROR_INVALID_SUBSCRIPTION = 103;
const WEBSOCKET_ERROR_URI_TOO_LONG = 104;
/** Consecutive open failures (no successful open in between) before `notifyPersistentFailure` fires. */
const CONSECUTIVE_FAILURE_NOTIFY_THRESHOLD = 3;

/** CoinMetrics documents a per-account "WebSocket API: Max Parallel Connections" limit of 200 for
 * Trial/Default(PRO)/Download(PRO) tiers (Rate limits — CoinMetrics API v4 documentation). This client
 * can only cap *its own* connection count against that; it has no visibility into (and can't coordinate
 * with) other browser tabs, sessions, or services concurrently using the same API key, so it cannot
 * guarantee the account-wide limit is never exceeded — only that this client never contributes more
 * than its own fair share. See `enforceConnectionLimit`. */
const COIN_METRICS_MAX_PARALLEL_CONNECTIONS = 200;

/**
 * Builds the client's standard error subscription response — the single envelope delivered to a
 * subscriber's callback (and, for persistent failures, `onError`) whenever a subscription can't receive
 * live data. Centralised so the `type`/`ts` shape stays identical across every rejection reason.
 */
function createErrorResponse(code: number, msg: string): ErrorSubscriptionResponse {
  return { type: 'error', ts: new Date().toISOString(), error: { code, msg } };
}

interface PhysicalConnection {
  /** `canonicalizeAssetsAndMetrics` result at the time this connection was opened — reconciled against
   * on every registration change to decide whether a reconnect is needed. */
  canonicalKey: string;
  /** The stream frequency this connection serves. */
  frequency: string;
  metrics: string[];
  /** This shard's own membership — the subset of its isolated group's addresses (see
   * `partitionAddressesToAvoidCrossSubscriberEviction`) whose streams actually reference one of this
   * shard's own assets (see `filterStreamsByAddressToAssets`) — NOT every address at this connection's
   * frequency, and not even every address in this shard's own group. Two different, isolated groups can
   * legitimately reference the same asset, so tick routing and failure notifications must scope themselves
   * to this specific connection's own membership; matching by frequency+asset alone would leak across
   * groups. Refreshed in place on every reconcile() (even one that reuses this same physical connection),
   * since a registration change that doesn't require a reconnect (e.g. one of several addresses sharing an
   * already-subscribed asset unregisters) must still stop routing to it immediately. */
  streamsByAddress: Map<string, CoinMetricsRequestStream[]>;
  /** Reverse index (asset -> addresses) derived from `streamsByAddress` — precomputed once per reconcile(),
   * not per tick, since `handleTick` is this class's hottest path. */
  reverseIndex: Map<string, Set<string>>;
  rxSubscription: RxJsSubscription;
}

/**
 * A CoinMetrics `/timeseries-stream/asset-metrics` client implementing the same `IWebSocketClient`
 * interface as Talos's own `WebSocketClient`, so it can be registered into `WebSocketClientProvider`
 * (via `useRegisterSocketClient`) and used through `useSubscription(request, { client: 'cm' })` like any
 * other subscription — no CoinMetrics-specific shape or behavior should ever need to reach a component
 * or hook outside this file.
 *
 * CoinMetrics has no subscribe/cancel/amend protocol — the asset list is fixed in the connection URL at
 * connect time, so changing it means closing and reopening the socket. This client simulates Talos's
 * per-address subscribe/update/cancel model on top of that by merging every registered address's
 * requested `{metric, asset}` pairs into one connection per distinct `frequency` (CoinMetrics' frequency
 * is a connection-level param that can't be unioned the way assets/metrics can — see
 * `computeConnectionKeyForStream`/`groupStreamsByFrequency`), and only reconnecting a given connection
 * when its merged set actually changes. One address's own registration can itself mix frequencies across
 * its streams, so grouping happens per stream, not per address — an address with streams at two
 * different frequencies ends up routed through two different connections.
 *
 * Before that merge happens, a frequency's addresses are partitioned so one address's metrics can never
 * poison another's (`partitionAddressesToAvoidCrossSubscriberEviction`) — without this, one address
 * registering enough metrics to blow CoinMetrics' URI limit would make *every* asset at that frequency
 * (via the global metrics union) classify as oversized, closing other addresses' already-healthy
 * connections for a metric increase they had nothing to do with. Each resulting group's merged asset list
 * is further split into URL-length-bounded shards (`chunkAssetsByUrlLength`) — CoinMetrics documents a
 * hard URI length limit (414 beyond it) that one large merged connection could otherwise exceed — and the
 * total number of physical connections this client opens is capped at CoinMetrics' documented per-account
 * parallel-connection limit (`enforceConnectionLimit`). Sharding only ever splits by asset, not by the
 * shared metrics list — an asset that's still over the limit even alone (or even isolated to its own
 * group) is reported as a failed subscription instead of opening a connection guaranteed to get rejected
 * (`notifyOversizedAssets`).
 *
 * `useSubscription`'s public `request` type has no way to require this client's own `frequency` field for
 * a `{ client: 'cm' }` call, so `registerSubscription`/`updateSubscription` validate every stream
 * themselves (`validateCoinMetricsRequestStream`) and reject an invalid one with an error response instead
 * of ever opening a connection CoinMetrics would just reject anyway. A rejection always guarantees no
 * active registration is left behind for that address — `useSubscription` itself has no synchronous way
 * to learn a call was rejected, so from its perspective the request is already gone the moment it calls
 * register/updateSubscription; leaving a rejected update's *prior* registration running would otherwise
 * keep delivering stale data for a subscription the hook already believes it moved on from.
 */
export class CoinMetricsWebSocketClient implements IWebSocketClient<unknown> {
  public isConnected = false;
  public sessionId: string | null = null;
  public performance = null;

  // ReplaySubject(1), not a plain Subject: WebSocketClientProvider subscribes via useRegisterSocketClient
  // — a React effect that fires on a *later* render than the one that calls connect() — so a plain
  // Subject's emission would already be gone by the time anything subscribes. Talos's own client avoids
  // this because WebSocketClientProvider subscribes before calling connect() in the same synchronous
  // block; this client has no such guarantee since registration happens through generic, decoupled
  // plumbing, so the emission needs to be replayable instead.
  public readonly onOpen = new ReplaySubject<string | null>(1);
  public readonly onError = new Subject<ErrorSubscriptionResponse>();
  public readonly onClose = new ReplaySubject<CloseEvent>(1);

  private readonly pongSubject = new Subject<never>();

  private baseUrl?: string;
  private readonly apiKey: string;

  private readonly streamsByAddress = new Map<string, CoinMetricsRequestStream[]>();
  private readonly callbacksByAddress = new Map<string, SubscriptionCallback<unknown>>();
  private readonly reqIdByAddress = new Map<string, number>();
  private readonly connectionsByKey = new Map<string, PhysicalConnection>();
  private blockedSubscriptions = new Set<string>();

  private reconcileScheduled = false;
  private nextReqId = 1;

  private readonly initialRetryMs: number;
  private readonly maxRetryMs: number;
  private readonly consecutiveFailureNotifyThreshold: number;
  private readonly maxUrlLength?: number;
  private readonly maxParallelConnections: number;

  constructor({
    apiKey,
    initialRetryMs = WS_INITIAL_RETRY_MS,
    maxRetryMs = WS_MAX_RETRY_MS,
    consecutiveFailureNotifyThreshold = CONSECUTIVE_FAILURE_NOTIFY_THRESHOLD,
    maxUrlLength,
    maxParallelConnections = COIN_METRICS_MAX_PARALLEL_CONNECTIONS,
  }: {
    apiKey: string;
    /** Overridable for fast, deterministic tests — production callers should rely on the defaults. */
    initialRetryMs?: number;
    maxRetryMs?: number;
    consecutiveFailureNotifyThreshold?: number;
    /** Forwarded to `chunkAssetsByUrlLength` — defaults to its own CoinMetrics-URI-limit-derived default
     * when omitted. Overridable so tests can force sharding with a small asset list. */
    maxUrlLength?: number;
    /** Overridable for fast, deterministic tests — production callers should rely on the default. */
    maxParallelConnections?: number;
  }) {
    this.apiKey = apiKey;
    this.initialRetryMs = initialRetryMs;
    this.maxRetryMs = maxRetryMs;
    this.consecutiveFailureNotifyThreshold = consecutiveFailureNotifyThreshold;
    this.maxUrlLength = maxUrlLength;
    this.maxParallelConnections = maxParallelConnections;
  }

  /**
   * Unlike Talos's client, there's no session handshake to wait for — `isConnected`/`onOpen` fire as
   * soon as the client has a base URL and is ready to accept registrations, not once some physical
   * per-asset socket happens to be open. This matters: `useSubscription` only calls `registerSubscription`
   * once its status is CONNECTED, but a physical socket here only opens *in response to* a registration
   * (CM has nothing to connect to with zero assets) — treating "connected" as "a socket is open" would
   * deadlock the two waiting on each other.
   */
  connect(url: string): void {
    this.baseUrl = url;
    this.isConnected = true;
    this.onOpen.next(null);
    this.scheduleReconcile();
  }

  closeAndReconnect(): void {
    this.closeAllPhysicalConnections();
    this.scheduleReconcile();
  }

  close(): void {
    this.closeAllPhysicalConnections();
    this.isConnected = false;
    // Without this, a reconcile already scheduled (via queueMicrotask) just before close() runs would
    // still fire afterward and see a truthy baseUrl, reopening a physical connection right after this
    // client was supposed to be fully torn down — e.g. useCoinMetricsSocketClient's effect cleanup calling
    // close() on API key rotation, racing a still-pending reconcile from another consumer's own
    // unregister in the same React commit. Clearing it makes reconcile()'s own guard a no-op instead.
    this.baseUrl = undefined;
    this.onClose.next({} as CloseEvent);
  }

  private closeAllPhysicalConnections(): void {
    for (const connection of this.connectionsByKey.values()) {
      connection.rxSubscription.unsubscribe();
    }
    this.connectionsByKey.clear();
  }

  /** CoinMetrics' stream is receive-only — there is nothing to publish. */
  registerPublication(): () => void {
    return () => {};
  }

  registerSubscription(
    address: string,
    streams: RequestStream[],
    callback: SubscriptionCallback<unknown>,
    _options?: { loadAll?: boolean; overrideParticipant?: string }
  ): () => void {
    const cmStreams = streams as CoinMetricsRequestStream[];

    const invalid = this.findInvalidStream(cmStreams);
    if (invalid) {
      logger.error(new Error(`[coinmetrics-ws] invalid subscription: ${invalid.reason}`), {
        extra: { address, stream: invalid.stream },
      });
      this.rejectAsync(
        callback,
        WEBSOCKET_ERROR_INVALID_SUBSCRIPTION,
        `Invalid CoinMetrics subscription: ${invalid.reason}`
      );
      // A rejection guarantees no active registration is left behind for this address — see
      // updateSubscription's own rejection branch for why this matters even though a fresh
      // registerSubscription call wouldn't normally have anything to clean up.
      this.unregisterSubscription(address);
      return () => {};
    }

    if (this.rejectIfBlocked(address, cmStreams, callback)) {
      return () => {};
    }

    this.streamsByAddress.set(address, cmStreams);
    this.callbacksByAddress.set(address, callback);
    this.reqIdByAddress.set(address, this.nextReqId++);
    this.scheduleReconcile();

    return () => this.unregisterSubscription(address);
  }

  updateSubscription(address: string, streams: RequestStream[], callback?: unknown): void {
    const cmStreams = streams as CoinMetricsRequestStream[];
    const validCallback = typeof callback === 'function' ? (callback as SubscriptionCallback<unknown>) : undefined;

    const invalid = this.findInvalidStream(cmStreams);
    if (invalid) {
      logger.error(new Error(`[coinmetrics-ws] invalid subscription update: ${invalid.reason}`), {
        extra: { address, stream: invalid.stream },
      });
      if (validCallback) {
        this.rejectAsync(
          validCallback,
          WEBSOCKET_ERROR_INVALID_SUBSCRIPTION,
          `Invalid CoinMetrics subscription: ${invalid.reason}`
        );
      }
      // useSubscription (packages/kyoko/src/hooks/useSubscription.ts) marks its own request active the
      // moment it calls updateSubscription, with no synchronous way to learn this client rejected it —
      // from its perspective the previous request is already gone. Leaving the prior registration's
      // physical connection running would keep delivering stale data for a subscription the hook itself
      // believes it already moved on from, so a rejected update tears it down instead: silence (correctly
      // reflecting "no active subscription"), not misleadingly-still-live old data.
      this.unregisterSubscription(address);
      return;
    }

    // Same hard block gate registerSubscription applies — an update must not be able to smuggle in a
    // blocked stream that a fresh registration would refuse. Tears down the prior (allowed) registration
    // too, so a subscriber that updates from an allowed metric to a blocked one goes silent rather than
    // continuing to receive its old data (mirrors the rejected-update teardown reasoning above).
    if (this.rejectIfBlocked(address, cmStreams, validCallback)) {
      return;
    }

    // Guards against an address with no prior successful registration reaching here — most plausibly one
    // whose earlier register/update call was invalid and torn down above — reqIdByAddress would otherwise
    // never gain an entry for it, and handleTick's `?? 0` fallback would tag every subsequent response
    // reqid: 0 forever. registerSubscription always allocates one; updateSubscription otherwise never did.
    const isUnknownAddress = !this.reqIdByAddress.has(address);

    // A single atomic mutation followed by one reconcile — NOT unregister-then-register. Doing it in two
    // steps could produce two reconnects instead of one, or a transient merged set missing an asset a
    // different address still needs.
    this.streamsByAddress.set(address, cmStreams);
    if (validCallback) {
      this.callbacksByAddress.set(address, validCallback);
    }
    if (isUnknownAddress) {
      this.reqIdByAddress.set(address, this.nextReqId++);
    }
    this.scheduleReconcile();
  }

  unregisterSubscription(address: string): void {
    this.streamsByAddress.delete(address);
    this.callbacksByAddress.delete(address);
    this.reqIdByAddress.delete(address);
    this.scheduleReconcile();
  }

  /** First stream in `streams` that isn't a well-formed CoinMetrics registration, or undefined if all are
   * valid — see `validateCoinMetricsRequestStream`'s own doc for why this check exists. */
  private findInvalidStream(
    streams: CoinMetricsRequestStream[]
  ): { stream: CoinMetricsRequestStream; reason: string } | undefined {
    for (const stream of streams) {
      const reason = validateCoinMetricsRequestStream(stream);
      if (reason) {
        return { stream, reason };
      }
    }
    return undefined;
  }

  /** Delivers an error response asynchronously — matching the timing a real registration would have
   * (never synchronous) — without ever touching `streamsByAddress`/opening a connection. */
  private rejectAsync(callback: SubscriptionCallback<unknown>, code: number, msg: string): void {
    setTimeout(() => callback(createErrorResponse(code, msg)), 0);
  }

  /**
   * If any of `streams` names a currently-blocked subscription, rejects it exactly as a fresh
   * registration would — logs, delivers an async error to `callback` (when one was supplied), and tears
   * down any prior registration for `address` so nothing keeps flowing — then returns true. Shared by
   * `registerSubscription` and `updateSubscription`: the block list is a hard configuration gate, not a
   * register-time-only check, so a mounted subscriber must not be able to slip past it by *updating* from
   * an allowed stream to a blocked one (the base Talos client gets this for free because its update path
   * re-enters register; this client's does not).
   */
  private rejectIfBlocked(
    address: string,
    streams: CoinMetricsRequestStream[],
    callback?: SubscriptionCallback<unknown>
  ): boolean {
    const blockedStream = streams.find(stream => this.blockedSubscriptions.has(stream.name));
    if (!blockedStream) {
      return false;
    }
    logger.info(`[coinmetrics-ws] ${blockedStream.name} subscription blocked by configuration`, {
      extra: { blockedStream: blockedStream.name, address },
    });
    if (callback) {
      this.rejectAsync(
        callback,
        WEBSOCKET_ERROR_SUBSCRIPTION_BLOCKED,
        `Subscription "${blockedStream.name}" is blocked by configuration`
      );
    }
    this.unregisterSubscription(address);
    return true;
  }

  /** CoinMetrics streams have no pagination. */
  pageSubscription(): void {}

  /** No-ops — see registerPublication's doc above: this stream is receive-only. */
  sendMessage(): void {}
  sendTimedMessage(): void {}
  ping(): void {}

  pongs() {
    return this.pongSubject.asObservable() as unknown as Observable<never>;
  }

  setBlockedSubscriptions(names: Set<string>): void {
    this.blockedSubscriptions = names;
  }

  /**
   * Coalesces N synchronous registration changes in one JS turn (e.g. several `useSubscription({client:
   * 'cm'})` callers mounting in the same React commit) into a single reconcile pass, instead of
   * reconnecting once per address.
   */
  private scheduleReconcile(): void {
    if (this.reconcileScheduled) {
      return;
    }
    this.reconcileScheduled = true;
    queueMicrotask(() => {
      this.reconcileScheduled = false;
      this.reconcile();
    });
  }

  private groupStreamsByConnectionKey(): Map<string, Map<string, CoinMetricsRequestStream[]>> {
    const grouped = new Map<string, Map<string, CoinMetricsRequestStream[]>>();
    for (const [address, streams] of this.streamsByAddress) {
      // Split by frequency first — one address's own streams can mix frequencies, and each distinct
      // frequency needs its own connection group (see the class docstring).
      for (const [key, streamsForKey] of groupStreamsByFrequency(streams)) {
        let group = grouped.get(key);
        if (!group) {
          group = new Map();
          grouped.set(key, group);
        }
        group.set(address, streamsForKey);
      }
    }
    return grouped;
  }

  private reconcile(): void {
    const baseUrl = this.baseUrl;
    if (!baseUrl) {
      return;
    }

    const groupedByFrequency = this.groupStreamsByConnectionKey();

    // Each frequency's addresses are first partitioned into groups that can never poison one another —
    // an address whose own metrics would push an already-fitting asset in the shared merge over the URI
    // limit is isolated into its own group instead, so its oversized footprint can only ever affect
    // itself (see partitionAddressesToAvoidCrossSubscriberEviction's own doc). Each group's merged asset
    // list is then split into as many URL-length-bounded shards as needed — almost always exactly one
    // group of exactly one shard in practice (a few dozen short asset ids across a handful of shared
    // metrics is nowhere near CoinMetrics' ~10 000-character URI limit), but never silently over it
    // regardless of how large the universe grows, and never at another address's expense. Every shard
    // carries the chunk-filtered subset of its group's streamsByAddress relevant to that shard's own
    // assets — NOT the frequency-wide map, and not even the whole group's map — so two different, isolated
    // groups that happen to reference the same asset never leak tick routing or failure notifications
    // into each other (see PhysicalConnection's own doc).
    const shardsBeforeLimit = Array.from(groupedByFrequency.entries()).flatMap(([frequency, streamsByAddress]) => {
      const groups = partitionAddressesToAvoidCrossSubscriberEviction({
        baseUrl,
        apiKey: this.apiKey,
        frequency,
        streamsByAddress,
        ...(this.maxUrlLength != null ? { maxUrlLength: this.maxUrlLength } : {}),
      });

      return groups.flatMap(({ assets, metrics, streamsByAddress: groupStreamsByAddress }) => {
        const { chunks, oversizedAssets } = chunkAssetsByUrlLength({
          baseUrl,
          apiKey: this.apiKey,
          assets,
          metrics,
          frequency,
          ...(this.maxUrlLength != null ? { maxUrlLength: this.maxUrlLength } : {}),
        });
        if (oversizedAssets.length > 0) {
          this.notifyOversizedAssets({ oversizedAssets, streamsByAddress: groupStreamsByAddress });
        }
        // Identifies the group by its own merged content, not by its array position — partitioning's
        // greedy "try existing groups first" order means array position shifts whenever an *unrelated*
        // address elsewhere is added/removed (e.g. one isolated group unregisters entirely, so a later
        // group shifts from index 1 to index 0), even though this group's own membership never changed.
        // A positional key would make reconcile()'s "is this connection already open unchanged" lookup
        // miss under the new key, forcing an avoidable reconnect on a healthy, untouched subscriber.
        // Content can't collide across groups (see partitionAddressesToAvoidCrossSubscriberEviction: a new
        // address always joins an existing group with identical content rather than forming a duplicate).
        const groupKey = canonicalizeAssetsAndMetrics({ assets, metrics });
        return chunks.map((assetChunk, shardIndex) => ({
          shardKey: `${frequency}#${groupKey}#${shardIndex}`,
          frequency,
          assets: assetChunk,
          metrics,
          // Filtered to just this chunk's own assets — NOT the whole group's streamsByAddress. A group
          // large enough to need more than one URL-length chunk would otherwise have every chunk sharing
          // the same full address membership, so dropping/failing one chunk (e.g. via
          // enforceConnectionLimit) could still wrongly notify an address whose stream actually lives in
          // a different, healthy chunk of the very same group.
          streamsByAddress: filterStreamsByAddressToAssets(groupStreamsByAddress, assetChunk),
          canonicalKey: canonicalizeAssetsAndMetrics({ assets: assetChunk, metrics }),
        }));
      });
    });

    const desiredShards = this.enforceConnectionLimit(shardsBeforeLimit);
    const desiredShardKeys = new Set(desiredShards.map(shard => shard.shardKey));

    // Close connections whose shard key no longer has any registrations at all — including a shard
    // dropped entirely by enforceConnectionLimit.
    for (const key of [...this.connectionsByKey.keys()]) {
      if (!desiredShardKeys.has(key)) {
        this.connectionsByKey.get(key)?.rxSubscription.unsubscribe();
        this.connectionsByKey.delete(key);
      }
    }

    for (const shard of desiredShards) {
      const existing = this.connectionsByKey.get(shard.shardKey);
      if (existing && existing.canonicalKey === shard.canonicalKey) {
        // Same physical connection reused — but this shard's own address membership may still have
        // changed (e.g. one address unregistered while another sharing the same asset did not, leaving
        // the merged canonicalKey unchanged) — refresh routing data immediately, not just on a reconnect.
        existing.streamsByAddress = shard.streamsByAddress;
        existing.reverseIndex = buildReverseIndex(shard.streamsByAddress);
        continue;
      }
      existing?.rxSubscription.unsubscribe();
      this.openConnection({
        connectionKey: shard.shardKey,
        frequency: shard.frequency,
        assets: shard.assets,
        metrics: shard.metrics,
        streamsByAddress: shard.streamsByAddress,
        canonicalKey: shard.canonicalKey,
      });
    }
  }

  /**
   * Caps the number of physical connections this client will open at CoinMetrics' documented per-account
   * "Max Parallel Connections" limit (see the constant's own doc for what this can and can't guarantee).
   * Reaching this from URL-length sharding alone would require an implausible number of subscribed assets
   * (each shard already holds close to a thousand short asset ids) — this is a backstop against a
   * misconfiguration or an unexpectedly large number of distinct frequencies, not a scenario expected in
   * normal operation. Truncates deterministically (first-come, in the order shards were computed),
   * logs the drop, and notifies every address whose streams fall in a dropped shard with an error
   * response, so the gap is visible rather than a silent "no live data, no explanation".
   */
  private enforceConnectionLimit<
    Shard extends {
      shardKey: string;
      frequency: string;
      assets: string[];
      streamsByAddress: ReadonlyMap<string, CoinMetricsRequestStream[]>;
    },
  >(shards: Shard[]): Shard[] {
    if (shards.length <= this.maxParallelConnections) {
      return shards;
    }

    const kept = shards.slice(0, this.maxParallelConnections);
    const dropped = shards.slice(this.maxParallelConnections);

    logger.error(
      new Error('[coinmetrics-ws] connection limit exceeded — some subscriptions will not receive live data'),
      {
        extra: {
          requestedConnections: shards.length,
          limit: this.maxParallelConnections,
          droppedShardKeys: dropped.map(shard => shard.shardKey),
        },
      }
    );

    // Scoped to exactly the dropped shards' own address membership — not a frequency+asset match against
    // every registered address — so an address in a different, kept shard that happens to share an asset
    // with a dropped one is never wrongly notified (see PhysicalConnection's own doc for why that
    // frequency+asset matching stopped being safe once isolated groups could overlap on an asset).
    const errorResponse = createErrorResponse(
      WEBSOCKET_ERROR_CONNECTION_LIMIT_EXCEEDED,
      'CoinMetrics connection limit exceeded — this subscription is not receiving live data.'
    );
    const notifiedAddresses = new Set<string>();
    for (const shard of dropped) {
      for (const address of shard.streamsByAddress.keys()) {
        if (!notifiedAddresses.has(address)) {
          notifiedAddresses.add(address);
          this.callbacksByAddress.get(address)?.(errorResponse);
        }
      }
    }

    return kept;
  }

  /**
   * Surfaces an asset that couldn't fit into any URL-length-bounded shard even alone (see
   * `chunkAssetsByUrlLength`'s `oversizedAssets` — only reachable when the shared `metrics`/overhead for a
   * group is itself large enough that no amount of asset-splitting helps) to every address in that same
   * group whose streams reference it, instead of opening a connection CoinMetrics would reject with 414.
   * Scoped to the specific group's own `streamsByAddress` — not every address at the frequency — for the
   * same cross-group-leakage reason `enforceConnectionLimit`'s notification is.
   */
  private notifyOversizedAssets({
    oversizedAssets,
    streamsByAddress,
  }: {
    oversizedAssets: string[];
    streamsByAddress: ReadonlyMap<string, CoinMetricsRequestStream[]>;
  }): void {
    logger.error(
      new Error(
        '[coinmetrics-ws] one or more assets exceed the URI length limit even alone — check the merged metrics list'
      ),
      { extra: { oversizedAssets } }
    );

    const oversizedAssetSet = new Set(oversizedAssets);
    const errorResponse = createErrorResponse(
      WEBSOCKET_ERROR_URI_TOO_LONG,
      'CoinMetrics subscription exceeds the maximum request URL length — this subscription is not receiving live data.'
    );
    for (const [address, streams] of streamsByAddress) {
      if (streams.some(stream => oversizedAssetSet.has(stream.tag))) {
        this.callbacksByAddress.get(address)?.(errorResponse);
      }
    }
  }

  private openConnection({
    connectionKey,
    frequency,
    assets,
    metrics,
    streamsByAddress,
    canonicalKey,
  }: {
    connectionKey: string;
    frequency: string;
    assets: string[];
    metrics: string[];
    streamsByAddress: Map<string, CoinMetricsRequestStream[]>;
    canonicalKey: string;
  }): void {
    if (!this.baseUrl || assets.length === 0 || metrics.length === 0) {
      return;
    }

    const url = buildConnectionUrl({ baseUrl: this.baseUrl, apiKey: this.apiKey, assets, metrics, frequency });

    // Tracks failures of this specific physical connection, independent of the client's public
    // isConnected/onOpen (which reflect readiness to accept registrations, not transport health — see
    // `connect()`). Reset only once a genuinely valid tick is parsed and routed — NOT merely once any
    // message arrives — since CM sends a distinct error envelope (`parseCoinMetricsErrorEnvelope`) for the
    // exact failure categories this exists to detect (unauthorized key, forbidden resource, invalid
    // request, rate limit), and treating that envelope as "a message arrived, so we must be fine" would
    // silently reset the counter every single failed attempt, defeating this detection entirely. A run of
    // failures with no valid tick received in between is what `notifyPersistentFailure` treats as
    // "probably not coming back on its own", and the browser WebSocket API doesn't expose HTTP status
    // codes for a failed handshake, so this is the only signal available.
    let consecutiveFailures = 0;
    let hasNotifiedFailure = false;
    // An error envelope already counts its attempt as a failure the moment it arrives (see the `next`
    // handler below) — set so the *subsequent* close/retry doesn't count that same attempt a second time.
    let failureAlreadyRecordedForCurrentAttempt = false;

    const socket$: WebSocketSubject<unknown> = webSocket({
      url,
      // Logging only — the client's public isConnected/onOpen/onClose reflect whether it's configured
      // and ready to accept registrations (see `connect()`), not any one physical connection's state.
      openObserver: {
        next: () => logger.info('[coinmetrics-ws] connected', { extra: { connectionKey } }),
      },
      closeObserver: {
        next: () => logger.info('[coinmetrics-ws] closed', { extra: { connectionKey } }),
      },
    });

    const recordFailure = () => {
      consecutiveFailures++;
      if (!hasNotifiedFailure && consecutiveFailures >= this.consecutiveFailureNotifyThreshold) {
        hasNotifiedFailure = true;
        // Read the connection's *current* streamsByAddress from connectionsByKey, not the streamsByAddress
        // parameter captured in this closure at open time: when this connection is reused across a
        // reconcile() whose merged canonicalKey happens not to change (e.g. a second address joins with
        // exactly the same asset/metric another address already has), reconcile() updates the stored
        // connection object in place rather than reopening it — this closure otherwise never learns about
        // that, and would keep notifying only whichever addresses existed when the connection first opened.
        const currentStreamsByAddress = this.connectionsByKey.get(connectionKey)?.streamsByAddress ?? streamsByAddress;
        this.notifyPersistentFailure(currentStreamsByAddress);
      }
    };

    const registerFailureAndComputeDelay = (retryCount: number) => {
      if (!failureAlreadyRecordedForCurrentAttempt) {
        recordFailure();
      }
      failureAlreadyRecordedForCurrentAttempt = false;
      return timer(computeBackoffDelayMs(retryCount, this.initialRetryMs, this.maxRetryMs));
    };

    const rxSubscription = socket$
      .pipe(
        // Connection-level error (e.g. a Cloudflare-related restart, or a persistent auth/entitlement
        // failure) — reconnect with backoff. RxJS calls a retry delay function as (error, retryCount);
        // only the count is needed here, so the error is explicitly discarded rather than accidentally
        // flowing into computeBackoffDelayMs as if it were the count (which would silently break the
        // backoff — Math.pow(2, someErrorObject) is NaN, collapsing the delay to ~0ms).
        retry({ count: Infinity, delay: (_error, retryCount) => registerFailureAndComputeDelay(retryCount) }),
        // Clean completion (shouldn't normally happen for a live stream) — reconnect with backoff.
        repeat({ count: Infinity, delay: count => registerFailureAndComputeDelay(count) })
      )
      .subscribe({
        next: raw => {
          const cmError = parseCoinMetricsErrorEnvelope(raw);
          if (cmError) {
            // Affirmative evidence of a real problem — CM's documented categories (unauthorized,
            // forbidden, invalid request, rate limit) are all things a reconnect alone won't fix, so this
            // counts toward persistent-failure detection immediately rather than waiting for the close
            // that (at least for the slow-client case) CM documents as following it.
            logger.error(new Error(`[coinmetrics-ws] CoinMetrics reported an error: ${cmError.type}`), {
              extra: { connectionKey, cmError },
            });
            failureAlreadyRecordedForCurrentAttempt = true;
            recordFailure();
            return;
          }

          if (this.handleTick(connectionKey, raw)) {
            consecutiveFailures = 0;
            hasNotifiedFailure = false;
            failureAlreadyRecordedForCurrentAttempt = false;
          }
        },
        // In practice unreachable — retry's infinite count means no error ever survives to reach here —
        // kept only as a defensive backstop against an unexpected rxjs edge case.
        error: error => {
          logger.error(error instanceof Error ? error : new Error('[coinmetrics-ws] stream error'), {
            extra: { error, connectionKey },
          });
        },
      });

    this.connectionsByKey.set(connectionKey, {
      canonicalKey,
      frequency,
      metrics,
      streamsByAddress,
      reverseIndex: buildReverseIndex(streamsByAddress),
      rxSubscription,
    });
  }

  /**
   * Surfaces a connection that has failed to open `consecutiveFailureNotifyThreshold` times in a row
   * (with no successful open in between) to every address in this specific shard's own group — scoped to
   * exactly `streamsByAddress`'s membership, not a frequency+asset match against every registered address,
   * so an address whose streams happen to live in a different, healthy shard (even one referencing the
   * same asset — see PhysicalConnection's own doc) is never wrongly alarmed. Surfaces via each affected
   * address's own subscription callback (so `useSubscription`'s error observable reflects it) and via the
   * client's own `onError`, for anything monitoring transport health directly rather than a specific
   * subscription. This is a "probably not coming back on its own" signal, not a definitive diagnosis —
   * retries continue in the background in case it does recover (e.g. a transient CM-side outage).
   */
  private notifyPersistentFailure(streamsByAddress: ReadonlyMap<string, CoinMetricsRequestStream[]>): void {
    const errorResponse = createErrorResponse(
      WEBSOCKET_ERROR_CONNECTION_FAILING,
      'CoinMetrics connection has failed repeatedly — check the API key, entitlements, or rate limits.'
    );

    this.onError.next(errorResponse);

    for (const address of streamsByAddress.keys()) {
      this.callbacksByAddress.get(address)?.(errorResponse);
    }
  }

  /** Returns whether `raw` was a genuinely valid, parsed CoinMetrics tick — the signal `openConnection`'s
   * `next` handler resets its failure counter on. Deliberately independent of whether any *currently*
   * registered address's streams actually matched it: a well-formed tick proves this connection is
   * authenticated and working, regardless of whether every affected registration happens to still exist. */
  private handleTick(connectionKey: string, raw: unknown): boolean {
    const connection = this.connectionsByKey.get(connectionKey);
    if (!connection) {
      return false;
    }

    const tick = parseAssetMetricTick(raw, connection.metrics);
    if (!tick) {
      return false;
    }

    // Scoped to this connection's own group membership (connection.streamsByAddress/reverseIndex,
    // refreshed by reconcile() on every registration change even when this physical connection is
    // reused) — NOT a frequency-wide index. Two different, isolated groups can reference the same asset
    // (see PhysicalConnection's own doc), so routing by frequency+asset alone would dispatch this tick to
    // an address whose actual subscription lives on a completely different physical connection.
    const addresses = connection.reverseIndex.get(tick.cmAssetId);
    if (!addresses) {
      return true;
    }

    for (const address of addresses) {
      const callback = this.callbacksByAddress.get(address);
      const streams = connection.streamsByAddress.get(address);
      if (!callback || !streams) {
        continue;
      }
      const metricNames = getMetricNamesForAddress(streams, tick.cmAssetId);
      const reqid = this.reqIdByAddress.get(address) ?? 0;
      const response = buildTickResponse({ type: COIN_METRICS_ASSET_METRIC_TICK, reqid, tick, metricNames });
      // null when none of this address's requested metrics are present on this tick — e.g. a different
      // address sharing this asset's connection asked for a metric this tick doesn't carry. Routing is
      // by asset only, so skip rather than deliver an empty, meaningless metrics: {} payload.
      if (response) {
        callback(undefined, response);
      }
    }

    return true;
  }
}
